import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AboutUsEntry } from "../Types/Types";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, formData: FormData) => void;
  initialData: AboutUsEntry;
  lang: string;
}

type Multi = { en: string; mk: string; sq: string };

const toMulti = (field: { [key: string]: string } | undefined): Multi => ({
  en: field?.en || "",
  mk: field?.mk || "",
  sq: field?.sq || "",
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "align", "list", "bullet", "link", "image",
];

const AboutUsEditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [name, setName] = useState<Multi>(toMulti(initialData.name));
  const [text, setText] = useState<Multi>(toMulti(initialData.text));
  const [subcategory, setSubcategory] = useState<Multi>(toMulti(initialData.subcategory));
  const [newImages, setNewImages] = useState<File[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]);

  useEffect(() => {
    setName(toMulti(initialData.name));
    setText(toMulti(initialData.text));
    setSubcategory(toMulti(initialData.subcategory));
    setExistingImageUrls(initialData.images || []);
  }, [initialData]);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const validImages = Array.from(e.target.files).filter((file) =>
        file.type.startsWith("image/")
      );
      setNewImages((prev) => [...prev, ...validImages]);
    }
  };

  const handleRemoveExistingImage = (url: string) => {
    setRemovedImages((prev) => [...prev, url]);
    setExistingImageUrls((prev) => prev.filter((img) => img !== url));
  };

  const handleRemoveNewImage = (index: number) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", JSON.stringify(name));
    formData.append("text", JSON.stringify(text));
    formData.append("subcategory", JSON.stringify(subcategory));

    existingImageUrls.forEach((url) => formData.append("images", url));
    newImages.forEach((image) => formData.append("images", image));

    if (removedImages.length > 0) {
      formData.append("removeImages", JSON.stringify(removedImages));
    }

    onSubmit(initialData._id, formData);
    onClose();
  };

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl relative max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-xl font-semibold mb-4 text-center text-brand-800">{t(`forms.editAbout`)}</h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t(`forms.name`)} ({langSuffix})</label>
            <input
              type="text"
              value={name[activeLang]}
              onChange={(e) => setField(setName, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t(`forms.subcategory`)} ({langSuffix})</label>
            <input
              type="text"
              value={subcategory[activeLang]}
              onChange={(e) => setField(setSubcategory, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t(`forms.description`)} ({langSuffix})</label>
            <ReactQuill
              key={`text-${activeLang}`}
              value={text[activeLang]}
              onChange={(value) => setField(setText, value)}
              theme="snow"
              className="h-48"
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
          <br />
          <br />
          <br />

          {existingImageUrls.length > 0 && (
            <div className="space-y-2">
              <label className="block font-medium">{t(`forms.existingImages`)}</label>
              <div className="flex gap-2 flex-wrap">
                {existingImageUrls.map((url, index) => (
                  <div key={index} className="relative w-24 h-24">
                    <img
                      src={url}
                      alt={`Existing ${index}`}
                      className="w-full h-full object-cover rounded"
                    />
                    <button
                      type="button"
                      className="absolute top-0 right-0 text-red-600"
                      onClick={() => handleRemoveExistingImage(url)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="block font-medium">{t(`forms.addImages`)}</label>
            <div className="flex gap-2 flex-wrap">
              {newImages.map((image, index) => (
                <div key={index} className="relative w-24 h-24">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index}`}
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    className="absolute top-0 right-0 text-red-600"
                    onClick={() => handleRemoveNewImage(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}

              <label
                htmlFor="editImageUpload"
                className="cursor-pointer border-2 border-dashed border-gray-400 p-4 rounded-md w-24 h-24 flex justify-center items-center text-gray-600 hover:bg-gray-100"
              >
                <span className="text-2xl">+</span>
                <input
                  id="editImageUpload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <FormActionButtons
            edit={true}
            onClose={onClose}
            onSubmit={handleSubmit}
            disabled={false}
          />
        </form>
      </div>
    </div>
  );
};

export default AboutUsEditModal;
